import axios, { type AxiosInterceptorManager, type AxiosResponse, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios'
import configResolver from '@/common/helpers/configResolver'
import { VITE_MODE } from '@/app/types/vite'

declare module 'axios' {
  export interface AxiosRequestConfig {
    raw?: boolean
  }
}

const axiosOptions = {
  baseURL: configResolver.pathname,
  debug: process.env.NODE_ENV === VITE_MODE.DEVELOPMENT,
  headers: {
    [configResolver.csrf.header]: configResolver.csrf.token,
  },
}

export const axiosInstance = axios.create(axiosOptions)

const addRequestInterceptor: AxiosInterceptorManager<InternalAxiosRequestConfig>['use'] = (onFulfilled, onRejected) => {
  return axiosInstance.interceptors.request.use(onFulfilled, onRejected)
}

const removeRequestInterceptor = (interceptorId: number) => {
  axiosInstance.interceptors.request.eject(interceptorId)
}

const addResponseInterceptor: AxiosInterceptorManager<AxiosResponse>['use'] = (onFulfilled, onRejected) => {
  return axiosInstance.interceptors.response.use(onFulfilled, onRejected)
}

const removeResponseInterceptor = (interceptorId: number) => {
  axiosInstance.interceptors.response.eject(interceptorId)
}

/**
 * @param {String} url
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
const getData = async <T>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
    const { data } = await axiosInstance.get<T>(url, config)
    return data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return Promise.reject(error.message)
    } else {
      return Promise.reject(error)
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const get = <T = any>(url: string, config: AxiosRequestConfig = {}) => {
  return axiosInstance.get<T>(url, config)
}

/**
 * @param {String} url
 * @param {Object} data
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const post = <T = any>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
  return axiosInstance.post<T>(url, data, config)
}

/**
 * @param {String} url
 * @param {Object} indata
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
const postData = async <T>(url: string, indata = {}, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
    const { data } = await axiosInstance.post<T>(url, indata, config)
    return data
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * @param {String} url
 * @param {Object} indata
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
const putData = async <T>(url: string, indata = {}, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
    const { data } = await axiosInstance.put<T>(url, indata, config)
    return data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return Promise.reject(error.message)
    } else {
      return Promise.reject(error)
    }
  }
}

/**
 * @param {String} url
 * @param {Object} data
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const put = <T = any>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
  return axiosInstance.put<T>(url, data, config)
}

/**
 * @param {String} url
 * @param {Object} data
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const patch = <T = any>(url: string, data = {}, config: AxiosRequestConfig = {}) => {
  return axiosInstance.patch<T>(url, data, config)
}

/**
 * @param {String} url
 * @param {Object} indata
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
const patchData = async <T>(url: string, indata = {}, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
    const { data } = await axiosInstance.patch<T>(url, indata, config)
    return data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return Promise.reject(error.message)
    } else {
      return Promise.reject(error)
    }
  }
}

/**
 * @param {String} url
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const remove = <T = any>(url: string, config: AxiosRequestConfig = {}) => {
  return axiosInstance.delete<T>(url, config)
}

/**
 * @param {String} url
 * @param {AxiosRequestConfig} config
 * @returns {Promise}
 */
const deleteData = async <T>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
    const { data } = await axiosInstance.delete<T>(url, config)
    return data
  } catch (error) {
    return Promise.reject(error)
  }
}

const isRejected = (input: PromiseSettledResult<unknown>): input is PromiseRejectedResult => input.status === 'rejected'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFulfilled = <T = any>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> => input.status === 'fulfilled'

export default {
  get,
  getData,
  post,
  postData,
  put,
  putData,
  patch,
  patchData,
  delete: remove,
  deleteData,
  isRejected,
  isFulfilled,
  addResponseInterceptor,
  removeResponseInterceptor,
  addRequestInterceptor,
  removeRequestInterceptor,
}
